// ════════════════════════════════════════════════════════════════════════════
//  ContaReceberEditDialog.tsx
//
//  Edição de uma conta a receber. Reusa o ContaReceberForm (o mesmo do
//  lançamento) pré-preenchido com o título carregado do servidor.
//
//  DUAS EDIÇÕES DIFERENTES, e é isso que a tela separa:
//   • PARCELAMENTO (valor/qtd/vencimento) → regera o carnê → o backend RECUSA
//     (422) se já houver recebimento. Por isso, com baixa, esses campos ficam
//     bloqueados e NÃO são enviados (senão o backend marcaria "mexeu no
//     parcelamento" e recusaria).
//   • CLASSIFICAÇÃO/metadados (conta contábil, tipo de despesas, nº documento,
//     descrição) → não toca as parcelas → o backend aceita SEMPRE, inclusive
//     depois de recebimentos. É o caso de corrigir a conta contábil de um título
//     que já teve pagamentos.
//
//  Espelho de ContaPagarEditDialog.
// ════════════════════════════════════════════════════════════════════════════

import { useMemo } from "react";
import { AlertCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ContaReceberForm } from "./ContaReceberForm";
import { useDetalheContaReceber } from "../hooks/useDetalheContaReceber";
import type { ContaReceberFormData } from "../schemas/contaReceberSchema";
import type { AtualizarContaReceberRequest } from "../types";

interface ContaReceberEditDialogProps {
  tituloId: number | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (id: number, data: AtualizarContaReceberRequest) => void;
  isLoading?: boolean;
}

const soData = (iso: string) => iso.slice(0, 10);

/**
 * Reconstrói o campo "dias entre parcelas" a partir dos vencimentos das parcelas
 * já gravadas: cada número é o deslocamento ABSOLUTO da parcela em relação à 1ª.
 */
function reconstruirDias(vencimentos: string[]): string {
  if (vencimentos.length <= 1) return "";
  const emDias = (iso: string) => {
    const [a, m, d] = soData(iso).split("-").map(Number);
    return new Date(a, m - 1, d).getTime();
  };
  const primeiro = emDias(vencimentos[0]);
  return vencimentos
    .slice(1)
    .map((v) => Math.round((emDias(v) - primeiro) / 86_400_000))
    .join(",");
}

export function ContaReceberEditDialog({
  tituloId, onOpenChange, onConfirm, isLoading,
}: ContaReceberEditDialogProps) {
  const { data: conta, isLoading: carregando } = useDetalheContaReceber(tituloId);

  // Qualquer recebimento trava só o PARCELAMENTO — a classificação segue livre.
  const temRecebimento = !!conta && (
    conta.isPago || conta.valorPago > 0 ||
    conta.analiticas.some((a) => a.isPago || a.valorPago > 0)
  );

  const defaultValues = useMemo<Partial<ContaReceberFormData> | undefined>(() => {
    if (!conta) return undefined;
    const parcelas = [...conta.analiticas].sort((a, b) => a.nroParcela - b.nroParcela);
    const vencs = parcelas.map((a) => a.dtVencimento);
    const qtd = conta.qtdParcelas > 0 ? conta.qtdParcelas : (vencs.length || 1);
    return {
      clienteId:    conta.clienteId,
      dtEmissao:    soData(conta.dtEmissao),
      dtVencimento: soData(vencs[0] ?? conta.dtVencimento),
      valorParcela: Number((conta.valorReceber / qtd).toFixed(2)),
      qtdParcelas:  qtd,
      diasParcelas: reconstruirDias(vencs.length ? vencs : [conta.dtVencimento]),
      diaFixo:      false,
      numDocumento: conta.numDocumento ?? undefined,
      lancamento:   conta.lancamento ?? undefined,
      contaContabilId: conta.contaContabilId ?? undefined,
      tipoDespesasId:  conta.tipoDespesasId ?? undefined,
    };
  }, [conta]);

  function submeter(data: ContaReceberFormData) {
    if (!conta) return;

    // Classificação + metadados: vão SEMPRE.
    const base: AtualizarContaReceberRequest = {
      clienteId:       data.clienteId,
      dtEmissao:       data.dtEmissao || undefined,
      numDocumento:    data.numDocumento,
      lancamento:      data.lancamento,
      contaContabilId: data.contaContabilId,
      tipoDespesasId:  data.tipoDespesasId,
    };

    // Parcelamento só é enviado quando NÃO há recebimento — senão o backend
    // detecta "mexeu no parcelamento" e recusa com 422.
    const payload: AtualizarContaReceberRequest = temRecebimento
      ? base
      : {
          ...base,
          dtVencimento: data.dtVencimento,
          valorParcela: data.valorParcela,
          qtdParcelas:  data.qtdParcelas,
          diasParcelas: data.diasParcelas || undefined,
          diaFixo:      data.diaFixo,
        };

    onConfirm(conta.id, payload);
  }

  return (
    <Dialog open={tituloId !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Conta a Receber</DialogTitle>
          <DialogDescription>
            {temRecebimento
              ? "Este título já tem recebimento. Corrija a classificação e os dados do documento; o parcelamento fica bloqueado."
              : "Altere os dados do título. Mudar valor, quantidade de parcelas ou vencimento regera o carnê."}
          </DialogDescription>
        </DialogHeader>

        {carregando || !conta ? (
          <div className="py-8 text-center text-muted-foreground">Carregando...</div>
        ) : conta.isCancelado ? (
          <div className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-4 text-sm">
            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
            <span>Este título está cancelado e não pode ser editado.</span>
          </div>
        ) : (
          <ContaReceberForm
            key={conta.id}
            defaultValues={defaultValues}
            bloquearParcelamento={temRecebimento}
            onSubmit={submeter}
            onCancel={() => onOpenChange(false)}
            isLoading={isLoading}
            submitLabel="Salvar alterações"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
